import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Space,
  Select,
  Input,
  Alert,
  notification,
} from "antd";
import {
  UserOutlined,
  BankOutlined,
  PhoneOutlined,
  SwapOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import EnrolleeIdentityStrip, { resolveEnrolleePlanLabel } from "../compoinents/EnrolleeIdentityStrip";
import { SUBMIT_CLAIM_LABEL, canSubmitClaimFor } from "./claimSubmission";
import { ADMIN_REVIEW_MODAL_CLASS } from "../dashboard/utils";
import { isCheckinCleanupSuperAdmin } from "../../components/Admin/CheckinCleanupPanel";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import { ENCOUNTER_CHANGE_TYPE_PERMISSIONS } from "./encounterPermissions";
import lib from "../../assets/utils/lib";
import PmMappingPanel from "../../components/PlanManagement/PmMappingPanel";
import "./EncounterDetailsModal.css";

const { TextArea } = Input;

const STATUS_MAP = { 0: "Active", 1: "Closed" };
const CHECKIN_TYPE_MAP = { 1: "In-Patient", 2: "Out-Patient" };
const CHECKIN_SOURCE_MAP = { 0: "Facility", 1: "Manual PA" };

function formatDateTime(value) {
  if (!value) return "Not available";
  try {
    return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );
  } catch {
    return value;
  }
}

function parseEnvelope(resp) {
  const body = resp?.data ?? resp;
  if (body && Number(body.error) === 0) return body.data;
  const message = body?.message || "Request failed";
  throw new Error(message);
}

export const EncounterDetailsModal = forwardRef(function EncounterDetailsModal(
  {
    data = {},
    userRoleId = 0,
    onViewPA,
    onCheckout,
    submitMode = false,
    onTypeChanged,
    onPmMapped,
    isCheckoutLoading = false,
  },
  ref,
) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [localData, setLocalData] = useState(data);
  // The encounter row carries no plan; the benefit panel resolves it from the
  // policy record (clients.plans_id), the same source the PA page shows.
  const [pmPlanName, setPmPlanName] = useState(null);
  const [pmRefresh, setPmRefresh] = useState(0);
  const planLabel = pmPlanName || resolveEnrolleePlanLabel(localData);
  const [changeOpen, setChangeOpen] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [targetTypeId, setTargetTypeId] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => setVisible(true),
    close: () => setVisible(false),
  }));

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const { hasAnyPermission } = usePermissions();
  // encounters.change_type (or admin); the legacy super user check stays as a fallback.
  const canChangeType = useMemo(
    () => (hasAnyPermission(ENCOUNTER_CHANGE_TYPE_PERMISSIONS) || isCheckinCleanupSuperAdmin(user))
      && Number(localData?.deleted || 0) !== 1,
    [hasAnyPermission, user, localData],
  );

  const loadTypes = async () => {
    setTypesLoading(true);
    try {
      const resp = await lib.listAdminCheckinTypes({ status: "active" });
      const list = parseEnvelope(resp);
      const rows = Array.isArray(list) ? list : list?.items || list?.types || [];
      const currentId = Number(localData?.checkinType);
      setTypeOptions(
        rows
          .map((row) => {
            // GET /v2/admin/checkin-types/ hydrates each row as { checkin_type: {...}, ... } —
            // fall back to the row itself in case a leaner shape is ever returned instead.
            const type = row.checkin_type || row;
            return {
              value: Number(type.checkin_type_id ?? type.id),
              label: type.checkin_type_name || type.name || `Type #${type.checkin_type_id ?? type.id}`,
              disabled: Number(type.checkin_type_id ?? type.id) === currentId,
            };
          })
          .filter((opt) => opt.value > 0),
      );
    } catch (e) {
      notification.error({ message: e?.message || "Failed to load check-in types" });
      setTypeOptions([]);
    } finally {
      setTypesLoading(false);
    }
  };

  const openChangeType = () => {
    setTargetTypeId(null);
    setReason("");
    setChangeOpen(true);
    loadTypes();
  };

  const submitChangeType = async () => {
    if (!localData?.code) return;
    if (!targetTypeId) {
      notification.warning({ message: "Select a target check-in type" });
      return;
    }
    setSaving(true);
    try {
      const result = parseEnvelope(
        await lib.changeCheckinType(localData.code, {
          checkin_type_id: Number(targetTypeId),
          reason: reason.trim() || undefined,
        }),
      );
      const to = result?.to || {};
      const next = {
        ...localData,
        checkinType: to.checkin_type_id ?? targetTypeId,
        checkin_type_id: to.checkin_type_id ?? targetTypeId,
        checkin_type_name: to.checkin_type_name || localData.checkin_type_name,
      };
      setLocalData(next);
      setChangeOpen(false);
      notification.success({
        message: "Check-in type updated",
        description: `${result?.from?.checkin_type_name || "Previous"} → ${to.checkin_type_name || targetTypeId}`,
      });
      onTypeChanged?.(next, result);
    } catch (e) {
      notification.error({ message: e?.message || "Failed to change check-in type" });
    } finally {
      setSaving(false);
    }
  };

  if (!localData?.code && !localData?.insurance_no) return null;

  const statusKey = Number(localData?.checkin_status) === 0 ? 0 : 1;
  const typeKey = Number(localData?.checkinType);
  const sourceKey = Number(localData?.source);
  const canViewPA = localData?.code && [1, 2, 3].includes(userRoleId);
  const canCheckout = submitMode
    ? canSubmitClaimFor(localData, userRoleId)
    : Number(localData?.checkin_status) === 0 && [1, 3].includes(userRoleId);

  const footer = [
    <Button key="close" onClick={() => setVisible(false)}>
      Close
    </Button>,
    canChangeType ? (
      <Button key="change-type" icon={<SwapOutlined />} onClick={openChangeType}>
        Change check-in type
      </Button>
    ) : null,
    canViewPA && onViewPA ? (
      <Button
        key="pa"
        onClick={() => {
          setVisible(false);
          onViewPA(localData);
        }}
      >
        Open pre-auth
      </Button>
    ) : null,
    canCheckout && onCheckout ? (
      <Button
        key="checkout"
        type="primary"
        loading={isCheckoutLoading}
        onClick={() => {
          setVisible(false);
          onCheckout(localData);
        }}
      >
        {submitMode ? SUBMIT_CLAIM_LABEL : "Checkout"}
      </Button>
    ) : null,
  ].filter(Boolean);

  return (
    <>
      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog encounter-details-modal`}
        open={visible}
        onCancel={() => setVisible(false)}
        footer={footer}
        width={720}
        destroyOnClose
        title={
          <div className="encounter-detail-modal__title">
            <span>Encounter details</span>
            <Tag color={statusKey === 0 ? "green" : "default"}>{STATUS_MAP[statusKey]}</Tag>
          </div>
        }
      >
        <div className="encounter-details-modal">
          <EnrolleeIdentityStrip
            client={localData}
            planLabel={planLabel !== '—' ? planLabel : undefined}
            datetime={localData?.checkin_date}
            facilityName={localData?.facility_name}
            extraSecondaryFacts={[
              localData?.code || null,
              CHECKIN_SOURCE_MAP[sourceKey] || null,
            ]}
          />

          <section className="encounter-detail-section">
            <div className="encounter-detail-section__head">
              <UserOutlined />
              Patient
            </div>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Insurance no">{localData?.insurance_no || "—"}</Descriptions.Item>
              <Descriptions.Item label="Policy no">{localData?.policy_no || "—"}</Descriptions.Item>
              <Descriptions.Item label="Plan">{planLabel}</Descriptions.Item>
              <Descriptions.Item label="Gender">{localData?.gender || "—"}</Descriptions.Item>
              <Descriptions.Item label="Role">{localData?.role || "—"}</Descriptions.Item>
              <Descriptions.Item label="State / LGA">
                {localData?.state && localData?.lga
                  ? `${localData.state.toUpperCase()} / ${localData.lga.toUpperCase()}`
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Primary facility">
                {localData?.primary_health_facility || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Secondary facility" span={2}>
                {localData?.secondary_health_facility || "—"}
              </Descriptions.Item>
            </Descriptions>
          </section>

          <section className="encounter-detail-section">
            <div className="encounter-detail-section__head">
              <BankOutlined />
              Facility
            </div>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Facility">{localData?.facility_name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Phone">
                {localData?.facility_phone ? (
                  <Space size={4}>
                    <PhoneOutlined />
                    {localData.facility_phone}
                  </Space>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                {localData?.facility_address || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Check-out">
                {localData?.checkout_date ? formatDateTime(localData.checkout_date) : "Pending"}
              </Descriptions.Item>
              <Descriptions.Item label="Check-in type">
                {localData?.checkin_type_name || CHECKIN_TYPE_MAP[typeKey] || localData?.checkinType || "—"}
              </Descriptions.Item>
            </Descriptions>
          </section>

          {localData?.code ? (
            <section className="encounter-detail-section">
              <div className="encounter-detail-section__head">
                <LinkOutlined />
                Benefit mapping
              </div>
              <PmMappingPanel
                checkinCode={localData.code}
                source="encounter"
                title="Map each item to its benefit"
                refreshKey={pmRefresh}
                onContext={(ctx) => setPmPlanName(ctx?.plan_name || null)}
                onMapped={() => {
                  setPmRefresh((n) => n + 1);
                  onPmMapped?.(localData.code);
                }}
              />
            </section>
          ) : null}

        </div>
      </Modal>

      <Modal
        title="Change check-in type"
        open={changeOpen}
        onCancel={() => !saving && setChangeOpen(false)}
        onOk={submitChangeType}
        okText="Update type"
        confirmLoading={saving}
        destroyOnClose
        okButtonProps={{ disabled: !targetTypeId }}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Admin correction"
            description="Use this to fix a wrongly typed visit (for example annual medicals filed under outpatient). The change is audited."
          />
          <div>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>Current type</div>
            <Tag color="geekblue">
              {localData?.checkin_type_name || CHECKIN_TYPE_MAP[typeKey] || `Type #${typeKey}` || "—"}
            </Tag>
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>New type</div>
            <Select
              style={{ width: "100%" }}
              placeholder="Select check-in type"
              loading={typesLoading}
              options={typeOptions}
              value={targetTypeId}
              onChange={setTargetTypeId}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>Reason (optional)</div>
            <TextArea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Filed under outpatient; should be annual medical"
              maxLength={500}
              showCount
            />
          </div>
        </Space>
      </Modal>
    </>
  );
});
